import { addDays, differenceInCalendarDays, format, isValid, parseISO } from 'date-fns';

import type { AwayScheduleBlock } from '@/services/getAwayScheduleV2';

/**
 * Pure helpers for the expert away calendar. Every date is a local calendar day
 * as a `YYYY-MM-DD` string, which sorts and compares correctly as text, so no
 * helper builds a `Date` just to compare two days. The caller passes `today`
 * in, which keeps these deterministic and lets a screen re-evaluate at midnight.
 */

export const MAX_AWAY_DAYS = 30;

type DateRange = Pick<AwayScheduleBlock, 'startDate' | 'endDate'>;

export type AwayValidationCode =
  | 'invalid_date_range'
  | 'start_in_past'
  | 'block_too_long'
  | 'overlaps_existing';

export type AwayValidationResult =
  | { valid: true }
  | { valid: false; code: AwayValidationCode; message: string };

export function toLocalDateString(date: Date): string {
  return format(date, 'yyyy-MM-dd');
}

export function getTodayDateString(now: Date = new Date()): string {
  return toLocalDateString(now);
}

/** Days covered by a range, counting both ends. */
export function countAwayDays({ startDate, endDate }: DateRange): number {
  return differenceInCalendarDays(parseISO(endDate), parseISO(startDate)) + 1;
}

/**
 * Joins a formatted date with non-breaking spaces, so a line never ends on
 * `Fri, Oct` with the day on the next. Text matchers treat it as a space.
 */
function keepTogether(formatted: string): string {
  return formatted.replace(/ /g, '\u00A0');
}

/** `Oct 28`, with the year added when it is not the current one. */
export function formatAwayDate(date: string, today: string): string {
  const parsed = parseISO(date);
  const sameYear = parsed.getFullYear() === parseISO(today).getFullYear();

  return keepTogether(format(parsed, sameYear ? 'MMM d' : 'MMM d, yyyy'));
}

export function formatAwayRange(range: DateRange, today: string): string {
  return `${formatAwayDate(range.startDate, today)} to ${formatAwayDate(range.endDate, today)}`;
}

function sortByStart(blocks: AwayScheduleBlock[]): AwayScheduleBlock[] {
  return [...blocks].sort((first, second) => first.startDate.localeCompare(second.startDate));
}

/** The block that contains today, both ends inclusive. */
export function getCurrentAwayBlock(
  blocks: AwayScheduleBlock[],
  today: string
): AwayScheduleBlock | undefined {
  return sortByStart(blocks).find((block) => block.startDate <= today && today <= block.endDate);
}

/** Blocks that start after today, soonest first. */
export function getUpcomingAwayBlocks(
  blocks: AwayScheduleBlock[],
  today: string
): AwayScheduleBlock[] {
  return sortByStart(blocks).filter((block) => block.startDate > today);
}

function isDateString(value: string): boolean {
  return /^\d{4}-\d{2}-\d{2}$/.test(value) && isValid(parseISO(value));
}

/**
 * The latest last day a block starting on `start` may have: `MAX_AWAY_DAYS` counting both ends,
 * except that a block already longer than that may be kept or shortened but never extended,
 * so its own end is the limit when the start is unchanged.
 */
export function getLongestEnd(start: string, editing?: DateRange): string {
  const capped = toLocalDateString(addDays(parseISO(start), MAX_AWAY_DAYS - 1));

  return editing && editing.startDate === start && editing.endDate > capped
    ? editing.endDate
    : capped;
}

/**
 * Mirrors the server's rules for a new block so a bad range is caught before
 * the request: both dates real, end not before start, start not in the past,
 * at most `MAX_AWAY_DAYS` counting both ends, and no overlap with an existing
 * block. The server remains the authority; this exists for friendly messages.
 */
export function validateAwayRange(
  range: DateRange,
  existingBlocks: AwayScheduleBlock[],
  today: string,
  /** The block being edited, when there is one. Its own start is exempt from the past rule. */
  editing?: DateRange
): AwayValidationResult {
  const { startDate, endDate } = range;

  if (!isDateString(startDate) || !isDateString(endDate)) {
    return { valid: false, code: 'invalid_date_range', message: 'Choose your first and last day.' };
  }

  if (endDate < startDate) {
    return {
      valid: false,
      code: 'invalid_date_range',
      message: 'Your last day can’t be before your first day.',
    };
  }

  const keepsStart = !!editing && startDate === editing.startDate;

  if (startDate < today && !keepsStart) {
    return {
      valid: false,
      code: 'start_in_past',
      message: 'Your first day can’t be in the past.',
    };
  }

  if (countAwayDays(range) > MAX_AWAY_DAYS && endDate > getLongestEnd(startDate, editing)) {
    return {
      valid: false,
      code: 'block_too_long',
      message: `Time away can last up to ${MAX_AWAY_DAYS} days. Choose an earlier last day.`,
    };
  }

  const clash = sortByStart(existingBlocks).find(
    (block) => startDate <= block.endDate && block.startDate <= endDate
  );

  if (clash) {
    return {
      valid: false,
      code: 'overlaps_existing',
      message: `These dates overlap your time away from ${formatAwayRange(clash, today)}.`,
    };
  }

  return { valid: true };
}

/**
 * The one period the UI shows: the block covering today, else the earliest
 * upcoming one. Any others (from admin or earlier use) are left alone.
 */
export function getAwayPeriod(
  blocks: AwayScheduleBlock[],
  today: string
): AwayScheduleBlock | undefined {
  return getCurrentAwayBlock(blocks, today) ?? getUpcomingAwayBlocks(blocks, today)[0];
}

/** `Tue, Nov 24`, the form the Availability row and sheet use. */
export function formatAwayDay(date: string, today: string): string {
  const parsed = parseISO(date);
  const sameYear = parsed.getFullYear() === parseISO(today).getFullYear();

  return keepTogether(format(parsed, sameYear ? 'EEE, MMM d' : 'EEE, MMM d, yyyy'));
}

export function getAvailabilityRowState({
  period,
  today,
  legacyAway,
}: {
  period: DateRange | undefined;
  today: string;
  legacyAway: boolean;
}): { subtitle: string; isAway: boolean } {
  if (legacyAway) {
    return { subtitle: 'Away, no end date', isAway: true };
  }

  if (period && period.startDate <= today && today <= period.endDate) {
    return {
      subtitle: `Away through ${formatAwayDay(period.endDate, today)}`,
      isAway: true,
    };
  }

  if (period) {
    return {
      subtitle: `Away starting ${formatAwayDay(period.startDate, today)}`,
      isAway: false,
    };
  }

  return { subtitle: 'Accepting inquiries', isAway: false };
}

export type RangeSelection = { start: string | null; end: string | null };

/**
 * Whether a day can be tapped. Days before today are never offered; there is no limit on how
 * far ahead a period may start. Picking a last day is limited to `MAX_AWAY_DAYS` counting both
 * ends from the start, or the block's own end if it is already longer and its start is
 * unchanged, so a long block can be shortened or kept but not extended. While a last day is
 * being picked, an earlier day stays tappable and moves the start there, which is what
 * `applyDayTap` does with it. A locked start (the block already began) is only ever picking a
 * last day, so earlier days stay refused.
 */
export function isDayTappable(
  selection: RangeSelection,
  day: string,
  today: string,
  locked: boolean,
  editing?: { startDate: string; endDate: string },
  /** The expert's other blocks, which a new range must not overlap. */
  otherBlocks: DateRange[] = []
): boolean {
  if (day < today || findBlockCovering(day, otherBlocks)) {
    return false;
  }

  const { start, end } = selection;
  const isPickingEnd = !!start && (locked || !end);

  if (!isPickingEnd) {
    return true;
  }

  // A day before the start moves the start there, unless the time away already began.
  if (day < start) {
    return !locked;
  }

  return day <= getLatestEnd(start, editing, otherBlocks);
}

function findBlockCovering(day: string, blocks: DateRange[]): DateRange | undefined {
  return blocks.find((block) => block.startDate <= day && day <= block.endDate);
}

/** The first other block that starts after `start`, which a range from `start` must stop before. */
function findNextBlock(start: string, blocks: DateRange[]): DateRange | undefined {
  return [...blocks]
    .filter((block) => block.startDate > start)
    .sort((first, second) => first.startDate.localeCompare(second.startDate))[0];
}

/**
 * The latest last day a range starting on `start` may have: the 30 day cap from `getLongestEnd`,
 * or the day before the next other block when that comes sooner, so the calendar never offers a
 * range that Save would refuse as an overlap.
 */
function getLatestEnd(start: string, editing: DateRange | undefined, blocks: DateRange[]): string {
  const capped = getLongestEnd(start, editing);
  const next = findNextBlock(start, blocks);
  const beforeNext = next ? toLocalDateString(addDays(parseISO(next.startDate), -1)) : capped;

  return beforeNext < capped ? beforeNext : capped;
}

/**
 * Why a day the calendar offers is not tappable, in the expert's words, so a tap on it
 * explains itself instead of doing nothing. Mirrors the branches of `isDayTappable`, which
 * stays the authority on whether a day can be tapped at all.
 */
export function getBlockedDayReason(
  selection: RangeSelection,
  day: string,
  today: string,
  locked: boolean,
  editing?: { startDate: string; endDate: string },
  otherBlocks: DateRange[] = []
): string | null {
  if (isDayTappable(selection, day, today, locked, editing, otherBlocks)) {
    return null;
  }

  if (day < today) {
    return 'That day has already passed.';
  }

  const covering = findBlockCovering(day, otherBlocks);

  if (covering) {
    return `You already have time away from ${formatAwayRange(covering, today)}.`;
  }

  const { start } = selection;

  if (!start) {
    return null;
  }

  const latestEnd = getLatestEnd(start, editing, otherBlocks);

  if (latestEnd < getLongestEnd(start, editing)) {
    return `It would overlap your other time away. The latest last day is ${formatAwayDay(latestEnd, today)}.`;
  }

  return `Time away can last up to ${MAX_AWAY_DAYS} days. The latest last day is ${formatAwayDay(latestEnd, today)}.`;
}

/** First tap sets the start, second the last day, a third starts over. */
export function applyDayTap(
  selection: RangeSelection,
  day: string,
  locked: boolean
): RangeSelection {
  if (locked) {
    return { ...selection, end: day };
  }

  if (!selection.start || selection.end) {
    return { start: day, end: null };
  }

  if (day < selection.start) {
    return { start: day, end: null };
  }

  return { ...selection, end: day };
}

export function getDayAfter(date: string): string {
  return toLocalDateString(addDays(parseISO(date), 1));
}
